import moment from 'moment-timezone';
import { RootState } from '../redux/store';
import { CalendarCheck, ExternalLink } from 'lucide-react';
import { Button } from './Button';
import { resetAppointment } from '../redux/slices/appointmentSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatSelectedSlot } from '../utils/helpers';

interface SuccessCardProps {
  className?: string;
  isSmartBooking?: boolean;
  success: { time: string; patientName: string; portalLink: string | null; portalLinkExpiry: number | null };
}

const expiryText = (seconds: number | null) =>
  seconds == null ? 'shortly' : moment.duration(seconds, 'seconds').humanize(true);

export const SuccessCard = ({ className = '', isSmartBooking, success }: SuccessCardProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { practice } = useSelector((s: RootState) => s.auth);
  const isManual = practice?.scheduler_type === 'manual';
  const showPortal = !!practice?.show_portal_link && !!success.portalLink;
  const { portalLink } = success;

  return (
    <div className={`min-h-full space-y-4 py-2 flex flex-col justify-center items-center text-center ${className}`}>
      <CalendarCheck size={60} className="text-success-700" />

      {isManual ? (
        <div className="space-y-2">
          <div className="text-xl font-semibold text-black">Thanks {success.patientName}!</div>
          <div className="text-base text-gray-600">
            We've received your request. We'll call you shortly for appointment confirmation.
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="text-xl font-normal text-black">
            {success.patientName}, we've got you<br />confirmed for your appointment
          </div>
          <div className="text-xl font-bold text-black">{formatSelectedSlot(success.time || '')}</div>
        </div>
      )}

      <Button
        title={isManual ? 'Request another appointment' : 'Book another appointment'}
        variant="secondary"
        onClick={() => {
          dispatch(resetAppointment());
          if (isSmartBooking) {
            navigate(`../smart-booking${location.search || ''}`);
          } else {
            navigate('../step-1');
          }
        }}
      />

      {/* Patient portal section — shown only when the practice enables it and a link came back. */}
      {showPortal && (
        <>
          <hr className="w-80 border-gray-200" />
          <div className="space-y-1">
            <div className="text-base text-gray-600">
              Open your patient portal to complete any remaining details.
            </div>
            <div className="text-sm text-gray-500">
              This link will expire {expiryText(success.portalLinkExpiry)}.
            </div>
          </div>
          <Button
            title={<span className="flex items-center gap-2"><ExternalLink size={18} /> Open patient portal</span>}
            onClick={() => window.open(portalLink!, '_blank', 'noopener,noreferrer')}
          />
        </>
      )}
    </div>
  );
};
